import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, Check } from "lucide-react";
import { toast } from "sonner";
import { subscribe } from "@/lib/api";

const CONTENT_UPGRADE_CATEGORIES = ["plateau", "side-effects", "undereating", "nutrition", "expectations"];

export function shouldShowContentUpgrade(article) {
  if (!article) return false;
  const slug = (article.slug || "").toLowerCase();
  const title = (article.title || "").toLowerCase();
  const hub = (article.hub || "").toLowerCase();
  const combined = `${slug} ${title} ${hub}`;
  return CONTENT_UPGRADE_CATEGORIES.some(cat => combined.includes(cat));
}

const ContentUpgradeModal = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribe({
        email,
        name,
        freebie_id: "21-protocol-errors",
        source: "content-upgrade",
        tags: ["21-errors-subscriber"],
      });
      setDone(true);
      toast.success("Check your email for the 21 Errors guide!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4" data-testid="content-upgrade-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl relative"
      >
        <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100" data-testid="content-upgrade-modal-close">
          <X className="w-5 h-5 text-gray-400" />
        </button>

        {done ? (
          <div className="text-center py-4" data-testid="content-upgrade-success">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: "#E8F0E4" }}>
              <Check className="w-7 h-7" style={{ color: "#2D5A3D" }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: "#1A1A1A" }}>Check your email!</h3>
            <p className="text-sm" style={{ color: "#4B5548" }}>Your 21 GLP-1 Protocol Errors guide is on its way.</p>
            <button onClick={onClose} className="mt-4 text-sm font-medium" style={{ color: "#2D5A3D" }}>Continue reading</button>
          </div>
        ) : (
          <>
            <h3 className="text-lg font-bold mb-1 pr-8" style={{ color: "#1A1A1A" }}>
              21 GLP-1 Protocol Errors
            </h3>
            <p className="text-sm mb-5" style={{ color: "#4B5548" }}>
              Already on GLP-1 and not seeing the results you expected? This free diagnostic guide reveals the 21 most common mistakes.
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Your first name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl text-sm border focus:outline-none"
                style={{ borderColor: "#E8E4DC", backgroundColor: "#FAFAF8" }}
                data-testid="content-upgrade-name"
              />
              <input
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2.5 rounded-xl text-sm border focus:outline-none"
                style={{ borderColor: "#E8E4DC", backgroundColor: "#FAFAF8" }}
                data-testid="content-upgrade-email"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50"
                style={{ backgroundColor: "#C8614A" }}
                data-testid="content-upgrade-submit"
              >
                {loading ? "Sending..." : "Get the Diagnostic Guide"}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
            <p className="text-xs text-center mt-3" style={{ color: "#6B6B6B" }}>
              For educational purposes only. Not medical advice. Always consult your prescribing healthcare provider.
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
};

export const ContentUpgradeCTA = ({ position = "mid" }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div
        className="my-10 bg-white rounded-2xl p-5 border"
        style={{ borderLeftWidth: "4px", borderLeftColor: "#E8F0E4", borderColor: "#E8E4DC", borderLeftStyle: "solid" }}
        data-testid={`content-upgrade-cta-${position}`}
      >
        <p className="text-base font-medium mb-2" style={{ color: "#1A1A1A" }}>
          Already on GLP-1 and not seeing the results you expected?
        </p>
        <p className="text-sm mb-3" style={{ color: "#4B5548" }}>
          Download the <strong>21 GLP-1 Protocol Errors</strong> guide — free. Find out which mistakes may be stalling your progress.
        </p>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity"
          style={{ backgroundColor: "#C8614A" }}
          data-testid={`content-upgrade-btn-${position}`}
        >
          Get the Diagnostic Guide <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <AnimatePresence>
        {showModal && <ContentUpgradeModal onClose={() => setShowModal(false)} />}
      </AnimatePresence>
    </>
  );
};

export default ContentUpgradeCTA;
